/**
 * Popover — floating panel anchored to a trigger.
 *
 * Wraps Radix Popover for the heavy lifting (portal, positioning, focus
 * management, click-outside, Escape). Use this primitive any time you want
 * to anchor a panel of arbitrary content to a trigger — account menus, hover
 * cards, settings popovers.
 *
 * Don't use this for menu-shaped content (lists of selectable items). For
 * that, prefer `DropdownMenu` (Radix) so screen readers get menu semantics.
 *
 * Surface area kept narrow on purpose. If you need positioning options
 * (offset, alignment) drop down to Radix Popover directly.
 */

import { forwardRef, type ReactNode, type Ref } from 'react';
import * as RadixPopover from '@radix-ui/react-popover';
import styles from './Popover.module.css';

export interface PopoverProps {
  /** The element that opens the panel — must be focusable. */
  trigger: ReactNode;
  /** Content of the panel. */
  children: ReactNode;
  /** Controlled open state. Omit to let the popover manage its own state. */
  open?: boolean;
  /** Called when open changes. Use with `open` for controlled mode. */
  onOpenChange?: (open: boolean) => void;
  /** Alignment of the panel against the trigger. */
  align?: 'start' | 'center' | 'end';
  /** Pixel offset between trigger and panel. Default 6. */
  sideOffset?: number;
  /** Optional className on the panel. */
  className?: string;
}

interface PopoverContentRefProps {
  contentRef?: Ref<HTMLDivElement>;
}

export const Popover = forwardRef<HTMLDivElement, PopoverProps & PopoverContentRefProps>(
  function Popover(
    { trigger, children, open, onOpenChange, align = 'end', sideOffset = 6, className, contentRef },
    _ref,
  ) {
    const composed = [styles.content, className ?? ''].filter(Boolean).join(' ');
    return (
      <RadixPopover.Root open={open} onOpenChange={onOpenChange}>
        <RadixPopover.Trigger asChild>{trigger}</RadixPopover.Trigger>
        <RadixPopover.Portal>
          <RadixPopover.Content
            ref={contentRef}
            className={composed}
            align={align}
            sideOffset={sideOffset}
          >
            {children}
          </RadixPopover.Content>
        </RadixPopover.Portal>
      </RadixPopover.Root>
    );
  },
);
